/**
 * Hero Slideshow — premium title carousel with edge peeks, meta fade-up,
 * background crossfade. GSAP-powered when available (loaded by
 * aykah-motion.js); graceful CSS fallback if GSAP is missing/blocked.
 *
 * Preserves: auto-rotate, dot navigation, keyboard (arrows), swipe, pause
 * on hover/focus, ARIA live region.
 */
(function () {
  'use strict';

  var container = document.getElementById('hero-slideshow');
  if (!container) return;

  var slides = container.querySelectorAll('.hero-slide');
  var titles = container.querySelectorAll('.hero-title');
  var metas = container.querySelectorAll('.hero-meta');
  var dots = container.querySelectorAll('.hero-dot');
  var speed = parseInt(container.getAttribute('data-auto-rotate') || '7000', 10);
  var total = slides.length;
  var current = 0;
  var paused = false;
  var animating = false;
  var timer = null;

  /* Title slide constants — single title visible at a time, slides in/out horizontally */
  var SLIDE_OFFSET_VW = 100;     // distance travelled during transition (full viewport)
  var TRANSITION_DURATION = 1.1; // seconds
  var TRANSITION_EASE = 'power3.inOut';

  /* ARIA setup */
  container.setAttribute('aria-roledescription', 'carousel');
  container.setAttribute('aria-label', 'Hero slideshow');
  container.setAttribute('role', 'region');
  container.setAttribute('tabindex', '0');

  var liveRegion = document.createElement('div');
  liveRegion.setAttribute('aria-live', 'polite');
  liveRegion.setAttribute('aria-atomic', 'true');
  liveRegion.className = 'sr-only';
  container.appendChild(liveRegion);

  slides.forEach(function (slide, i) {
    slide.setAttribute('aria-hidden', i === 0 ? 'false' : 'true');
    if (i === 0) { slide.removeAttribute('inert'); } else { slide.setAttribute('inert', ''); }
    slide.setAttribute('role', 'group');
    slide.setAttribute('aria-roledescription', 'slide');
    slide.setAttribute('aria-label', 'Slide ' + (i + 1) + ' of ' + total);
  });

  /* These are carousel controls, NOT tabs. role="tab" is only legal inside a
     role="tablist" parent, and the slides above are already the carousel
     pattern (role="group" + aria-roledescription="slide"), so a tablist here
     would contradict them. aria-current marks the active dot instead, and
     every dot stays reachable by Tab. */
  dots.forEach(function (dot, i) {
    if (i === 0) { dot.setAttribute('aria-current', 'true'); }
    dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
  });

  /* Wait for GSAP (loaded async by aykah-motion.js) then initialize.
     Fall back to CSS-only mode after 3s if GSAP never shows up. */
  function whenGSAP(maxMs, callback) {
    if (window.gsap) return callback(window.gsap);
    var started = Date.now();
    var iv = setInterval(function () {
      if (window.gsap) {
        clearInterval(iv);
        callback(window.gsap);
      } else if (Date.now() - started >= maxMs) {
        clearInterval(iv);
        callback(null);
      }
    }, 50);
  }

  var goTo; /* assigned by whichever setup runs */

  whenGSAP(3000, function (gsap) {
    if (gsap && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setupGSAP(gsap);
    } else {
      setupFallback();
    }
    wireControls();
    if (total > 1) startTimer();
  });

  function setupGSAP(gsap) {
    /* Initial static state: active title at center, all others hidden in place. */
    function setInitialTitles(activeIndex) {
      titles.forEach(function (title, i) {
        gsap.set(title, {
          xPercent: -50,
          yPercent: -50,
          x: 0,
          scale: 1,
          autoAlpha: i === activeIndex ? 1 : 0
        });
      });
    }

    /* Transition: desktop = directional slide; mobile = pure fade (narrower
       viewports don't have room for two titles to pass through center without
       overlapping, and swipe gestures already carry the directional intent). */
    function transitionTitles(prevIndex, nextIndex, direction) {
      if (!titles.length) return;
      var isDesktop = window.matchMedia('(min-width: 768px)').matches;

      if (isDesktop) {
        var outTargetX = (direction === 1 ? -SLIDE_OFFSET_VW : SLIDE_OFFSET_VW) + 'vw';
        var inStartX = (direction === 1 ? SLIDE_OFFSET_VW : -SLIDE_OFFSET_VW) + 'vw';

        gsap.to(titles[prevIndex], {
          x: outTargetX,
          autoAlpha: 0,
          duration: TRANSITION_DURATION,
          ease: TRANSITION_EASE,
          overwrite: 'auto',
          onComplete: function () { gsap.set(titles[prevIndex], { x: 0 }); }
        });

        gsap.fromTo(
          titles[nextIndex],
          { x: inStartX, autoAlpha: 0 },
          { x: 0, autoAlpha: 1, duration: TRANSITION_DURATION, ease: TRANSITION_EASE, overwrite: 'auto' }
        );
      } else {
        /* Mobile: slide with fast outgoing fade. Outgoing slides the full
           distance but its opacity drops to 0 in the first 40% of the
           transition (power2.in) — so it's invisible before the incoming
           title enters the visible area. Keeps the directional slide motion
           while eliminating the two-titles-at-once overlap on narrow
           viewports. Property-level overwrite:'auto' means the x tween and
           autoAlpha tween don't kill each other. */
        var outTargetXM = (direction === 1 ? -SLIDE_OFFSET_VW : SLIDE_OFFSET_VW) + 'vw';
        var inStartXM = (direction === 1 ? SLIDE_OFFSET_VW : -SLIDE_OFFSET_VW) + 'vw';

        gsap.to(titles[prevIndex], {
          x: outTargetXM,
          duration: TRANSITION_DURATION,
          ease: TRANSITION_EASE,
          overwrite: 'auto',
          onComplete: function () { gsap.set(titles[prevIndex], { x: 0 }); }
        });
        gsap.to(titles[prevIndex], {
          autoAlpha: 0,
          duration: TRANSITION_DURATION * 0.4,
          ease: 'power2.in',
          overwrite: 'auto'
        });

        gsap.fromTo(
          titles[nextIndex],
          { x: inStartXM, autoAlpha: 0 },
          { x: 0, autoAlpha: 1, duration: TRANSITION_DURATION, ease: TRANSITION_EASE, overwrite: 'auto' }
        );
      }
    }

    function layoutMetas(activeIndex, animate) {
      if (!metas.length) return;
      metas.forEach(function (meta, i) {
        var isActive = i === activeIndex;
        meta.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        if (isActive) { meta.removeAttribute('inert'); } else { meta.setAttribute('inert', ''); }
        if (!animate) {
          gsap.set(meta, { autoAlpha: isActive ? 1 : 0, y: 0 });
          return;
        }
        if (isActive) {
          gsap.fromTo(
            meta,
            { autoAlpha: 0, y: 18 },
            {
              autoAlpha: 1,
              y: 0,
              duration: TRANSITION_DURATION * 0.65,
              ease: 'power2.out',
              delay: TRANSITION_DURATION * 0.35,
              overwrite: 'auto'
            }
          );
        } else {
          gsap.to(meta, {
            autoAlpha: 0,
            duration: TRANSITION_DURATION * 0.35,
            ease: 'power2.out',
            overwrite: 'auto'
          });
        }
      });
    }

    /* Initial static layout (no animation). Clear the inline style
       hints that prevented FOUC so GSAP has full control. */
    titles.forEach(function (t) {
      t.style.opacity = '';
      t.style.visibility = '';
      t.style.transform = '';
    });
    metas.forEach(function (m) { m.classList.remove('invisible'); });

    /* Initial background image state — first slide visible, others hidden.
       No scale/zoom effect (flat images). */
    slides.forEach(function (slide, i) {
      gsap.set(slide, { autoAlpha: i === 0 ? 1 : 0, xPercent: 0 });
      var img = slide.querySelector('.hero-slide-img');
      if (img) gsap.set(img, { scale: 1 });
    });

    setInitialTitles(0);
    layoutMetas(0, false);

    goTo = function (index) {
      if (index === current || animating) return;
      animating = true;
      var prev = current;
      current = index;

      /* Background — layered reveal. Outgoing stays fully visible at opacity 1
         behind the incoming, which fades IN on top (higher z-index). At no
         point is any pixel partially transparent over nothing, so there's no
         washed-out/grey moment mid-transition. Timing matches the title slide. */
      slides[prev].setAttribute('aria-hidden', 'true');
      slides[prev].setAttribute('inert', '');
      slides[index].setAttribute('aria-hidden', 'false');
      slides[index].removeAttribute('inert');

      /* Ensure outgoing is fully visible below; incoming starts hidden on top. */
      gsap.set(slides[prev], { autoAlpha: 1, zIndex: 1 });
      gsap.set(slides[index], { zIndex: 2 });

      gsap.fromTo(
        slides[index],
        { autoAlpha: 0 },
        {
          autoAlpha: 1,
          duration: TRANSITION_DURATION,
          ease: TRANSITION_EASE,
          overwrite: 'auto',
          onComplete: function () {
            /* Reveal complete — outgoing is fully covered, safe to hide it. */
            gsap.set(slides[prev], { autoAlpha: 0, zIndex: 0 });
          }
        }
      );

      /* Determine direction for the slide transition */
      var direction = (index > prev) ? 1 : -1;
      /* Handle wrap-around so auto-rotate from last→first still feels "forward" */
      if (index === 0 && prev === total - 1) direction = 1;
      else if (index === total - 1 && prev === 0) direction = -1;

      /* GSAP — title slide transition + meta fade-up */
      transitionTitles(prev, index, direction);
      layoutMetas(index, true);

      /* Dots */
      updateDot(prev, false);
      updateDot(index, true);

      /* Screen reader announcement */
      liveRegion.textContent = 'Slide ' + (index + 1) + ' of ' + total;

      setTimeout(function () { animating = false; }, TRANSITION_DURATION * 1000 + 50);
    };
  }

  function setupFallback() {
    /* No GSAP (or user prefers reduced motion). Use simple opacity swap. */
    titles.forEach(function (t, i) {
      t.style.opacity = i === 0 ? '1' : '0';
      t.style.visibility = '';
      t.style.transition = 'opacity 600ms ease';
    });
    metas.forEach(function (m, i) {
      m.style.opacity = i === 0 ? '1' : '0';
      m.style.transition = 'opacity 500ms ease';
      m.classList.remove('invisible');
    });

    goTo = function (index) {
      if (index === current) return;
      var prev = current;
      current = index;

      slides[prev].classList.replace('opacity-100', 'opacity-0');
      slides[prev].setAttribute('aria-hidden', 'true');
      slides[prev].setAttribute('inert', '');
      slides[index].classList.replace('opacity-0', 'opacity-100');
      slides[index].setAttribute('aria-hidden', 'false');
      slides[index].removeAttribute('inert');

      titles.forEach(function (t, i) { t.style.opacity = i === index ? '1' : '0'; });
      metas.forEach(function (m, i) {
        m.style.opacity = i === index ? '1' : '0';
        m.setAttribute('aria-hidden', i === index ? 'false' : 'true');
      });

      updateDot(prev, false);
      updateDot(index, true);

      liveRegion.textContent = 'Slide ' + (index + 1) + ' of ' + total;
    };
  }

  function updateDot(i, active) {
    var span = dots[i].querySelector('span');
    if (active) {
      span.className = 'hero-dot-fill hero-dot-on';
      dots[i].setAttribute('aria-current', 'true');
    } else {
      span.className = 'hero-dot-fill hero-dot-off';
      dots[i].removeAttribute('aria-current');
    }
  }

  function next() { goTo((current + 1) % total); }
  function prev() { goTo((current - 1 + total) % total); }

  function startTimer() {
    stopTimer();
    timer = setInterval(function () {
      if (!paused && !animating) next();
    }, speed);
  }

  function stopTimer() {
    if (timer) { clearInterval(timer); timer = null; }
  }

  function wireControls() {
    /* Prev / Next arrow buttons (bottom-left of hero) */
    var prevBtn = container.querySelector('.hero-prev');
    var nextBtn = container.querySelector('.hero-next');
    if (prevBtn) prevBtn.addEventListener('click', function () { prev(); startTimer(); });
    if (nextBtn) nextBtn.addEventListener('click', function () { next(); startTimer(); });

    /* Dot click */
    dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var idx = parseInt(dot.getAttribute('data-dot-index'), 10);
        if (idx !== current) { goTo(idx); startTimer(); }
      });
    });

    /* Keyboard */
    container.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault(); next(); startTimer();
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault(); prev(); startTimer();
      }
    });

    /* Touch swipe */
    var touchStartX = 0;
    var SWIPE_THRESHOLD = 50;
    container.addEventListener('touchstart', function (e) {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });
    container.addEventListener('touchend', function (e) {
      var diff = touchStartX - e.changedTouches[0].screenX;
      if (Math.abs(diff) > SWIPE_THRESHOLD) {
        if (diff > 0) next(); else prev();
        startTimer();
      }
    }, { passive: true });

    /* Pause on hover / focus */
    container.addEventListener('mouseenter', function () { paused = true; });
    container.addEventListener('mouseleave', function () { paused = false; });
    container.addEventListener('focusin', function () { paused = true; });
    container.addEventListener('focusout', function () { paused = false; });
  }
})();
